import { ChevronDown, ChevronUp } from "baseui/icon";
import moment from "moment/moment";
import React, { FC, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AvatarPlaceholder } from "../../../../../../components/avatar/AvatarPlaceholder";
import Button from "../../../../../../components/button/Button";
import { LottieLoading } from "../../../../../../components/graphics/LottieLoading";
import { ListPager } from "../../../../../../components/pagination/ListPager";
import {
  getInitialsFromFullName,
  inactiveStatusId,
} from "../../../../../../constants/constants";
import { updatePaymentStatusFactory } from "../../../../../../factories/billing/factory-update-payment-status";
import { TimePeriod } from "../../../../../../hooks/useDetectTimePeriod";
import { usePaymentFiltersController } from "../../../../../../hooks/usePaymentFiltersController";
import {
  IPayment,
  SortEnum,
  useGetPaymentsQuery,
  useGetPaymentStatusesQuery,
  useSetPaymentLabelMutation,
} from "../../../../../../services/BillingService";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import { IOrganizationContact } from "../../../../../../types/types";
import DropStatus from "../../../../../billing/DropStatus";
import { OrganizationPaymentsFilters } from "../../../../../billing/payments/OrganizationPaymentsFilters";
import ErrorPopup from "../../../../modals/ErrorPopup";
import { navigateToContact } from "../../../../utils/navigateToContacts";

interface Props {
  customerId: string;
  organization: IOrganizationContact;
}

const PAGE_SIZE = 20;

const OrganizationPayments: FC<Props> = ({ customerId, organization }) => {
  const navigate = useNavigate();
  const isActive = organization?.contactStatusEnumId !== inactiveStatusId;

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ name: SortEnum; order: boolean }>({
    name: SortEnum.PaymentDate,
    order: false,
  });

  const [activePaymentId, setActivePaymentId] = useState("");
  const [isOpenError, setIsOpenError] = useState(false);

  const filters = usePaymentFiltersController();
  const isAllDateFilter =
    !filters.filter.period || filters.filter.period === TimePeriod.All;

  const { data: statuses, isLoading: isLoadingStatuses } =
    useGetPaymentStatusesQuery(getLinkedFirm()?.orgId || "");

  const [updatePayment, { isLoading: isLoadingUpdate }] =
    useSetPaymentLabelMutation();

  const updateStatus = (statusId: string, payment: IPayment) => {
    const body = updatePaymentStatusFactory.createModelFromDto({
      payment,
      status: statusId || null,
    });
    updatePayment(body);
  };

  const {
    data: payments,
    isLoading,
    isFetching,
  } = useGetPaymentsQuery({
    pageNumber: page,
    pageSize: PAGE_SIZE,
    orgId: getLinkedFirm()?.orgId,
    filterByDate: isAllDateFilter ? [] : ["Custom"],
    fromDate: isAllDateFilter
      ? null
      : filters.from
      ? filters.from.toISOString()
      : null,
    toDate: isAllDateFilter
      ? null
      : filters.to
      ? filters.to.toISOString()
      : null,
    sortBy: sort.name,
    sortOrder: sort.order ? "asc" : "desc",
    filterByStatus: [],
    filterByClient: [customerId],
    filterByAssignee: [],
    filterByLabel: filters.filterByLabel.map((item) => item.value),
  });

  const handleSort = (name: SortEnum) => {
    if (name === sort.name) {
      setSort({ name, order: !sort.order });
    } else {
      setSort({ name, order: true });
    }
  };

  const totalRecords = payments?.[0]?.totalRecords ?? 0;

  if (isLoading || isLoadingStatuses) {
    return <LottieLoading />;
  }

  const renderSortIcon = (name: SortEnum) => {
    if (sort.name !== name) return null;

    return (
      <div className={"relative left-0 top-[-4px]"}>
        {sort.order ? (
          <div className={"absolute"}>
            <ChevronUp size={26} />
          </div>
        ) : (
          <div className={"absolute"}>
            <ChevronDown size={26} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={"mb-4 flex items-end justify-between"}>
        <OrganizationPaymentsFilters controller={filters} />

        <Button
          label={"+ Add Payment"}
          size={"custom"}
          extraClasses={"btn-sm px-2"}
          onClick={() =>
            !isActive
              ? setIsOpenError(true)
              : navigate(`/billing/payments/add?customerId=${customerId}`)
          }
        />
      </div>

      <table className={"w-full"}>
        <thead className={"border-b-[1px] border-gray-300"}>
          <tr>
            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.Customer)}>
                <div>Contact</div>
                {renderSortIcon(SortEnum.Customer)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.PaymentDate)}>
                <div>Payment date</div>
                {renderSortIcon(SortEnum.PaymentDate)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.Amount)}>
                <div>Amount</div>
                {renderSortIcon(SortEnum.Amount)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.PaymentMethod)}>
                <div>Method</div>
                {renderSortIcon(SortEnum.PaymentMethod)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.PaymentStatus)}>
                <div>Label</div>
                {renderSortIcon(SortEnum.PaymentStatus)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.Notes)}>
                <div>Notes</div>
                {renderSortIcon(SortEnum.Notes)}
              </div>
            </th>
          </tr>
        </thead>

        <tbody className={"text-sm"}>
          {payments?.map((payment) => (
            <tr
              key={payment.paymentId}
              className={
                "cursor-pointer border-b-[1px] border-gray-300 last-of-type:border-none hover:bg-gray-100"
              }
              onClick={() =>
                navigate("/billing/payments/" + payment?.paymentId)
              }>
              <td className={"bg-transparent px-[16px] py-[8px]"}>
                <div className={"flex items-center"}>
                  <div className={"flex items-center text-[14px]"}>
                    <AvatarPlaceholder
                      size={"extra-small"}
                      type={payment?.businessContactId ? "purple" : "blue"}
                      label={getInitialsFromFullName(
                        payment?.customerName || "",
                      )}
                    />
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          navigateToContact({
                            type: payment?.businessContactId
                              ? "organization"
                              : "contact",
                            customerId: payment?.customerId || "",
                            id: payment?.businessContactId || "",
                          }),
                        );
                      }}
                      className={
                        "link-primary cursor-pointer pl-2 text-sm font-semibold hover:underline"
                      }>
                      {payment?.customerName}
                    </div>
                  </div>
                </div>
              </td>

              <td
                className={
                  "bg-transparent px-4 py-2 text-sm font-semibold text-gray-800"
                }>
                {moment(payment?.paymentDate).format("MMM DD, YYYY")}
              </td>

              <td
                className={
                  "bg-transparent px-4 py-2 text-sm font-semibold text-gray-800"
                }>
                {"$" +
                  (payment?.amount || 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
              </td>

              <td
                className={
                  "bg-transparent px-4 py-2 text-sm font-semibold text-gray-800"
                }>
                {payment?.paymentMethodName}
              </td>

              <td className={"bg-transparent px-4 py-2"}>
                <DropStatus
                  invoiceStatus={null}
                  isLoading={
                    (isLoadingUpdate || isFetching) &&
                    payment?.paymentId === activePaymentId
                  }
                  paymentStatus={payment?.paymentStatus || null}
                  statuses={statuses || []}
                  selectStatus={(status) => {
                    setActivePaymentId(payment?.paymentId);
                    updateStatus(status?.id || "", payment);
                  }}
                />
              </td>

              <td
                className={
                  "max-w-[300px] truncate whitespace-nowrap bg-transparent px-4 py-2 text-sm font-semibold text-gray-800"
                }>
                {payment?.notes}
              </td>
            </tr>
          ))}

          <tr className={"sticky bottom-0 left-0 bg-gray-100"}>
            <td className={"px-4 py-2"}></td>
            <td
              className={"px-4 py-2 text-end text-sm font-bold text-gray-800"}>
              Total:
            </td>
            <td className={"px-4 py-2 text-sm font-bold text-gray-800"}>
              $
              {(payments?.[0]?.sumOfAmount || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </td>
            <td className={"px-4 py-2"}></td>
            <td className={"px-4 py-2"}></td>
            <td className={"px-4 py-2"}></td>
          </tr>
        </tbody>
      </table>

      {!!payments?.length && (
        <ListPager
          page={page}
          pageSize={PAGE_SIZE}
          total={totalRecords}
          disabled={isFetching}
          onChange={setPage}
          className={"mt-2 px-0"}
        />
      )}

      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
    </>
  );
};

export default OrganizationPayments;
